/* @flow */
/* eslint-env es6,browser */

import * as u from '../utils';

/**
 * @global
 * @typedef hbConfig
 * @property {Array<string>} positions
 * @property {Object} posTokenMap
 * @property {string} cVar
 * @property {string} [exclude]
 * @property {number} [timeout=2000]
 * @property {string} [script='']
 * @property {string} [initTimeout=1000]
 * @property {string} [bidUrl='']
 * @property {string} [beaconUrl='']
 */
/**
 * @description Provides interface for all bidders and provide common functionality
 * @requires module:utils
 * @property {string} _name
 * @property {hbConfig} _config
 * @property {number} [_bInited=0] init status; 0 - NOT_INITED, 1 - INITED, -1 - FAILED
 * @property {Array<string>} _excludeList browser exclude list
 * @property {Array<string>} _mandatoryConfigs list of mandatory configs to be checked in init
 * @property {string} [_posId='id'] position specific key in posTokenMap
 * @property {string} [_isSupported=true] whether partner is supported or not. populated by partner constructor
 * @property {Object} _bidReponses response object used by renderAd for rendering
 * @property {string} [_renderType='html'] rendering type; html - doc write ad html, iframe - load given url in iframe, func - call given func with id
 * @property {Function} _renderFunc
 */
class _bidbase {
  _config: $Subtype<hbPartnerConfig>;
  _name: string;
  _bInited: initStatus;
  _initCheck: initChecker;
  _DefConfig: $Subtype<hbPartnerConfig>;
  //browser exclude list support
  _excludeList: Array<string>;
  _mandatoryConfigs: Array<string>;
  _posId: string;
  _bidResponses: Object;
  _renderType: string;
  _renderFunc: Function;
  _isSupported: boolean;
  _bidPriceDiscount: number;
  _enableReporting: boolean;

  constructor(name: string) {
    this._name = name;
    this._bInited = u.F_NOT_INITED;
    this._initCheck = {
      context: {},
      func: ''
    };
    this._DefConfig = {
      positions: [],
      exclude: '',
      cVar: '',
      timeout: 2000,
      posTokenMap: {},
      script: '',
      initTimeout: 1000,
      bidUrl: '',
      beaconUrl: ''
    };
    this._config = u.extend(true, {}, this._DefConfig);
    //browser exclude list support
    this._excludeList = [];
    this._mandatoryConfigs = [];
    this._posId = 'id';
    this._bidResponses = {};
    this._renderType = 'html';
    this._renderFunc = u.noop;
    this._isSupported = true;
    this._bidPriceDiscount = 1;
    this._enableReporting = true;
  }

  /**
   * getter for name
   * @returns {string} - Partner name
   */
  getName() {
    return this._name;
  }
  /**
   * get list of bid positions supported by this header bidder
   * @param {string} pName - page name
   * @returns {Array<string>} - Array of ad positions
   */
  getBidPositions(pName: string): Array<string> {
    return !this._isSupported || u.isBrowserExcluded(pName, this._excludeList)
      ? []
      : this._config.positions;
  }

  /**
   * Reset & Update config specific to this bidder
   * @param {hbConfig} oConfig - Partner config
   * @returns {undefined}
   */
  updateConfig(oConfig: hbPartnerConfig) {
    this._config = u.extend(true, {}, this._DefConfig, oConfig);
    this._bidPriceDiscount = (100 - (this._config.bidDiscount || 0)) / 100;
    //compute exclusion list again
    this._excludeList = u.getBrowserExcludeList(this._config.exclude);
  }

  /**
   * getter for qpkey
   * @param {string} pos - Position name
   * @returns {string} - QP key
   */
  getQPKey(pos: string): string {
    return u.isObject(this._config.posTokenMap[pos]) &&
      this._config.posTokenMap[pos].qpKey
      ? this._config.posTokenMap[pos].qpKey
      : this._config.qpKey || '';
  }

  /**
   * getter for posKey
   * @param {string} pos - Position name
   * @returns {string} - Position key
   */
  getPosKey(pos: string): string {
    return u.isObject(this._config.posTokenMap[pos])
      ? this._config.posTokenMap[pos][this._posId]
      : '';
  }

  /**
   * getter for lineId
   * @param {string} pos - Position name
   * @returns {string} - Line ID
   */
  getLineId(pos: string): string {
    return u.isObject(this._config.posTokenMap[pos])
      ? this._config.posTokenMap[pos].lineId
      : '';
  }

  /**
   * getter for cVar
   * @returns {string} - Custom Var
   */
  getCVar(): string {
    return this._config.cVar;
  }

  /**
   * Default implementation of preInit. If required bidder implementation should override this.
   * @returns {initStatus} - Pre-init status
   * @access private
   */
  _preInit(): initStatus {
    return u.F_INITED;
  }

  /**
   *
   * Default implementation for postInit. Bidders can override it
   * @returns {initStatus}  - Post-init status
   * @memberof _bidbase
   */
  _postInit(): initStatus {
    return u.F_INITED;
  }

  /**
   * checks for mandatory config
   * @returns {initStatus} - Config check status
   * @access private
   */
  _configCheck(): initStatus {
    //mandatory config string check
    let invalidConfigs = this._mandatoryConfigs.reduce(
      (acc, conf) => acc.concat(!this._config[conf].length ? conf : []),
      []
    );
    if (invalidConfigs.length) {
      u.logError(
        `${this._name} config error: missing ${invalidConfigs.join(',')}`
      );
      return u.F_FAILED;
    }

    //presence of posTokenMap object
    if (!u.isObject(this._config.posTokenMap)) {
      u.logError(`${this._name} config error: missing posTokenMap`);
      return u.F_FAILED;
    }

    //all positions in positions array should be configured in posTokenMap
    let misPos = this._config.positions.filter(
      pos => !this._config.posTokenMap[pos]
    );
    if (misPos.length) {
      u.logError(
        `${
          this._name
        } config error: missing positions in posTokenMap ${misPos.join(',')}`
      );
      return u.F_FAILED;
    }

    //all positions in positions array should have lineId in posTokenMap
    misPos = this._config.positions.filter(pos => !this.getLineId(pos));
    if (misPos.length) {
      u.logError(
        `${
          this._name
        } config error: missing lineId in posTokenMap ${misPos.join(',')}`
      );
      return u.F_FAILED;
    }

    return u.F_INITED;
  }

  /**
   * Default implementation of _externalScriptCheck. If required bidder implementation should override this.
   * @returns {initStatus} - External script check
   * @access private
   */
  _externalScriptCheck(): initStatus {
    let { context, func, obj } = this._initCheck;
    const cxtCheck = context === window || u.isObject(context);
    if (
      !cxtCheck ||
      (obj && u.isString(obj) && obj.length && !u.isObject(context[obj])) ||
      (func && u.isString(func) && func.length && !u.isFunction(context[func]))
    ) {
      return u.F_FAILED;
    }
    return u.F_INITED;
  }

  /**
   * _init - init functionality
   * extended classes need to override _preInit if any additional functionality is required
   * @returns {Promise} - Promise with init status
   * @access private
   */
  _init(): Promise<*> {
    if (this._bInited === u.F_TIMEOUT) {
      let extStatus = u.F_NOT_INITED;
      if ((extStatus = this._externalScriptCheck()) === u.F_FAILED) {
        u.logError(`${this._name} _externalScriptCheck failed`);
        this._bInited = extStatus;
        return Promise.reject();
      }
      if (extStatus === u.F_INITED) {
        this._bInited = this._postInit();
      } else {
        this._bInited = extStatus;
      }
      return Promise.resolve();
    }

    if (this._bInited === u.F_INITED) {
      return Promise.resolve();
    }

    if (this._bInited === u.F_FAILED) {
      return Promise.reject();
    }

    if (this._configCheck() === u.F_FAILED) {
      this._bInited = u.F_FAILED;
      return Promise.reject();
    }

    if (this._preInit() === u.F_FAILED) {
      u.logError(`${this._name} preInit failed`);
      this._bInited = u.F_FAILED;
      return Promise.reject();
    }

    if (this._config.beaconUrl.length) {
      u.loadJS(this._config.beaconUrl, document, true);
    }

    let loadPromise = Promise.resolve();
    if (this._config.script.length) {
      loadPromise = new Promise((resolve, reject) => {
        u
          .loadJSWithTimeout(
            this._config.script,
            document,
            true,
            this._config.initTimeout
          )
          .then(
            () => {
              if (this._externalScriptCheck() === u.F_INITED) {
                resolve();
              } else {
                // setting TIMEOUT to check externel script load during next init
                // though script is loaded, script execution is not guaranteed
                this._bInited = u.F_TIMEOUT;
                u.logError(`${this._name} _externalScriptCheck failed`);
                reject();
              }
              return this._bInited;
            },
            () => {
              // script load failed, it could be due to timeout
              // setting TIMEOUT to check externel script load during next init
              this._bInited = u.F_TIMEOUT;
              reject();
            }
          );
      });
    }

    return loadPromise.then(() => {
      this._bInited = this._postInit();
      u.logDebug(`${this._name} Init is done. getting ad bids`);
      return this._bInited;
    });
  }

  /**
   * get bids default implementation return zero bids.
   * Needs to override in bidders.
   * @param {Array<string>} posList - HB positions list
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {Promise<Object.<string, hbPosData>>} - Return promise
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    return Promise.resolve(ret);
  }

  /**
   * init and get bids
   * @param {Array<string>} posList - list of ad positions
   * @param {Object.<string, hbPosData>} ret - header bidding default response
   * @return {Promise<Object.<string, hbPosData>>} - Promise
   */
  getBids(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    return this._init().then(() => this._getAds(posList, ret), () => ret);
  }

  /**
   * render ad for this partner
   * @param {window} win - window object
   * @param {window.document} doc - document object
   * @param {string} pos - ad position
   * @param {string} id - Position DOM ID
   * @returns {undefined}
   */
  renderAd(win: window, doc: window.document, pos: string, id: string) {
    try {
      if (this._renderType === 'func') {
        this._renderFunc.call(window, doc, id);
      } else if (this._renderType === 'html') {
        let res = this._bidResponses[id];
        doc.write(res.ad);
        if (res.nurl && res.nurl.length) {
          doc.write(`<img src="${res.nurl}">`);
        }
      } else if (this._renderType === 'iframe') {
        var newIf = u.createIframe(
          doc,
          `${this._name}-${id}`,
          '100%',
          '100%',
          this._bidResponses[id].ad
        );
        doc.body.appendChild(newIf);
      } else {
        u.logError(`${this._name} render ad invalid render type`);
      }
    } catch (e) {
      u.logError(`${this._name} render ad failure ${e.message}`);
    }
  }

  /**
   * Validates RTB response format
   * @param {Object} resp - RTB response object
   * @returns {boolean} - valid RTB
   * @access private
   */
  _isValidRTBResponse(resp: Object): boolean {
    if (
      u.isObject(resp) &&
      u.isArray(resp.seatbid) &&
      resp.seatbid.length &&
      u.isArray(resp.seatbid[0].bid) &&
      resp.seatbid[0].bid.length
    ) {
      return true;
    }
    return false;
  }

  /**
   * Populates ret object with info, QP & CV
   * Stores response in bidResponses for use in renderAd later
   * @param {hbPartnerData} ret - Partner Data
   * @param {string} pos - Position name
   * @param {string} price - Bid price
   * @param {string} cVar - custom var
   * @param {string} ad - Ad
   * @param {string} nurl - nurl
   * @returns {undefined}
   * @access private
   */
  _handleBidPosData(
    ret: hbPartnerData,
    pos: string,
    price: string,
    cVar: string,
    ad?: string = '',
    nurl?: string = ''
  ) {
    let adjustedPrice = this._bidPriceAdjust(price);
    ret[pos].info = this._enableReporting
      ? `${this.getLineId(pos)}|${adjustedPrice}`
      : '';
    u.logDebug(
      `${this._name} pos:${pos} price:${price} adjprice:${adjustedPrice}`
    );

    //query param string to pass to OpenX ad call
    ret[pos]._QP[this.getQPKey(pos)] = adjustedPrice;
    ret[pos]._CV[this._config.cVar] = cVar;
    if (ad && ad.length) {
      this._bidResponses[cVar] = { ad: ad, nurl: nurl ? nurl : '' };
    }
  }

  /**
   * - Adjust bid price (cpm)
   * @param {string|number} price - Bid price to adjust
   * @returns {string|number} - Adjusted price
   * @access private
   */
  _bidPriceAdjust(price: string | number): string | number {
    let nprice = parseFloat(price);
    if (this._bidPriceDiscount === 1 || isNaN(nprice) || nprice <= 0) {
      return price;
    }
    return (nprice * this._bidPriceDiscount).toFixed(4);
  }

  /**
   * - getter for _enableReporting
   * @returns {boolean} - _enableReporting
   * @access private
   */
  _doReporting(): boolean {
    return this._enableReporting;
  }
}

export default _bidbase;
